import { ReactNode, useEffect, useRef } from 'react';

import { type AssignableItem, AssignWizard, AssignWizardHandle } from 'pages/custom_tests/components/assign_wizard';
import { useCustomTestEdit } from 'pages/custom_tests/components/custom_test_edit/context';
import { CustomTestApi, CustomTestAssignmentDetail } from 'apis/CustomTestApi';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { useCustomTests } from 'pages/custom_tests/context';
import { StudentGroupApi } from 'apis/StudentGroupApi';
import { TransactionApi } from 'apis/TransactionApi';
import { useNotification } from 'app/notifications';
import { Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

export const AssignmentsTab = props => {

    const [state, setState] = useSuperState({
        assignedAccessCodes: [] as AssignableItem[],
        assignedClassrooms: [] as AssignableItem[],
        assignmentDetails: [] as CustomTestAssignmentDetail[],
        error: null as string,
        loading: true
    });
    const { assignedAccessCodes, assignedClassrooms, assignmentDetails, error, loading } = state;

    const { selectedPartner } = useApp();
    const { permissions } = useCustomTests();
    const { name, assignmentRefreshKey, isActive, isOwner, modeId, refreshAssignments, testId, totalAssignmentCount = 0 } = useCustomTestEdit();
    const { createToast } = useNotification();
    const { formatMessage } = useIntl();

    const assignWizardRef = useRef<AssignWizardHandle>(null);

    const fetchAssignments = async () => {

        try {

            setState({ error: null, loading: true });

            const [transactions, groups, assignedInfo, assignmentDetails] = await Promise.all([
                TransactionApi.GetAll(),
                StudentGroupApi.GetAll('Partner'),
                CustomTestApi.GetAssignedTransactionsAndGroups(testId, selectedPartner.id),
                CustomTestApi.GetAssignmentDetails(testId, selectedPartner.id)
            ]);

            const assignedAccessCodes: AssignableItem[] = transactions
                .filter(t => assignedInfo.AssignedTransactionIds.includes(t.Id))
                .map(t => ({
                    CreatedDate: t.CreatedDateString,
                    Id: t.Id,
                    Identifier: t.Name,
                    UsesRemaining: t.UsesRemaining ?? 0
                }));

            const assignedClassrooms: AssignableItem[] = groups
                .filter(g => assignedInfo.AssignedStudentGroupIds.includes(g.Id))
                .map(g => ({
                    CreatedDate: g.DateCreate,
                    Description: g.Description ?? '',
                    Id: g.Id,
                    Name: g.Name
                }));

            setState({
                assignedAccessCodes,
                assignedClassrooms,
                assignmentDetails: assignmentDetails ?? [],
                loading: false
            });

        } catch (e) {

            setState({
                error: e?.message || e,
                loading: false
            });

        }

    };

    const launchAssignmentWizard = async () => {

        const assigned = await assignWizardRef.current?.show({ modeId, testId, testName: name });

        if (assigned) {

            createToast(
                formatMessage({ id: 'assignments_saved_successfully.22d8fe61', defaultMessage: 'Assignments saved successfully.' }),
                'success',
                5000
            );

            refreshAssignments();

        }

    };

    useEffect(() => {

        fetchAssignments();

    }, [assignmentRefreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const getModeLabel = (id: number) => id === 1
        ? (formatMessage({ id: 'training.1fce7301', defaultMessage: 'Training' }))
        : id === 3
            ? (formatMessage({ id: 'both.8074bfd4', defaultMessage: 'Both' }))
            : (formatMessage({ id: 'testing.a439953c', defaultMessage: 'Testing' }));

    // Mode renders as '{label} Mode' per the mock; 'Both' pluralizes to 'Both Modes'.
    const getModeValue = (id: number) => id === 3
        ? (formatMessage({ id: 'both_modes.fdac3d2d', defaultMessage: 'Both Modes' }))
        : `${getModeLabel(id)} ${formatMessage({ id: 'mode.f65b1f2b', defaultMessage: 'Mode' })}`;

    const detailFor = (targetType: 'transaction' | 'group', targetId: number) => assignmentDetails.find(detail => detail.TargetType === targetType && detail.TargetId === targetId);

    const renderDates = (detail?: CustomTestAssignmentDetail) => {

        const startDate = detail?.StartDate ?? '';
        const dueDate = detail?.DueDate ?? '';

        if (!startDate && !dueDate) return formatMessage({ id: 'no_date_restriction.e941d4c3', defaultMessage: 'No date restriction' });

        return (
            <span>
                {startDate ? <FormattedDate value={new Date(startDate)} /> : '—'}
                {' – '}
                {dueDate ? <FormattedDate value={new Date(dueDate)} /> : '—'}
            </span>
        );

    };

    const renderMaxAttempts = (detail?: CustomTestAssignmentDetail) => detail?.MaxAttempts
        ? String(detail.MaxAttempts)
        : (formatMessage({ id: 'unlimited.91630800', defaultMessage: 'Unlimited' }));

    const renderChip = (on: boolean, label: string) => on
        ? (
            <span className={'badge rounded-pill border'} style={{ backgroundColor: 'rgba(13, 110, 253, 0.1)', borderColor: 'rgba(13, 110, 253, 0.35)', color: '#0d6efd' }}>
                {label}
            </span>
        )
        : (
            <span className={'badge rounded-pill border bg-light text-muted'}>
                {label}
            </span>
        );

    const renderFeatures = (detail?: CustomTestAssignmentDetail) => {

        const proctoringOn = detail?.IsProctored ?? false;
        const reviewOn = detail?.AllowReview ?? false;

        return (
            <span className={'d-flex flex-wrap gap-2'}>
                {renderChip(proctoringOn, proctoringOn
                    ? (formatMessage({ id: 'proctoring_on.d9aef5fb', defaultMessage: 'Proctoring On' }))
                    : (formatMessage({ id: 'proctoring_off.17e8bf7c', defaultMessage: 'Proctoring Off' })))}
                {renderChip(reviewOn, reviewOn
                    ? (formatMessage({ id: 'question_review_on.06668e45', defaultMessage: 'Question Review On' }))
                    : (formatMessage({ id: 'question_review_off.5bde89aa', defaultMessage: 'Question Review Off' })))}
            </span>
        );

    };

    const renderField = (label: string, value: ReactNode, last: boolean) => (
        <div className={`d-flex align-items-start gap-3 py-2 ${last ? '' : 'border-bottom'}`}>
            <span className={'text-muted text-nowrap'} style={{ minWidth: '9rem' }}>
                {label}
            </span>
            <span className={'fw-semibold'}>
                {value}
            </span>
        </div>
    );

    const renderAssignmentCard = (key: string, leftFields: { label: string; value: ReactNode }[], rightFields: { label: string; value: ReactNode }[]) => (
        <div className={'border rounded bg-light px-3 py-2'} key={key}>
            <Row className={'gx-4'}>
                <Col md={6} xs={12}>
                    {leftFields.map((field, index) => (
                        <div key={index}>
                            {renderField(field.label, field.value, index === leftFields.length - 1)}
                        </div>
                    ))}
                </Col>
                <Col md={6} xs={12}>
                    {rightFields.map((field, index) => (
                        <div key={index}>
                            {renderField(field.label, field.value, index === rightFields.length - 1)}
                        </div>
                    ))}
                </Col>
            </Row>
        </div>
    );

    return (
        <div className={'assignments-tab d-flex flex-column'}>
            {loading ?
                (
                    <div className="d-flex align-items-center justify-content-center p-4">
                        <GMetrixLoader size={'large'} />
                    </div>
                )
                : error ?
                    (
                        <div className={'alert alert-danger d-flex align-items-center justify-content-between gap-3'}>
                            <span>
                                <FormattedMessage defaultMessage="Failed to load assignments." id="failed_to_load_assignments.58d2f8d1" />
                            </span>
                            <GMetrixButton
                                iconClass={'fa-solid fa-arrow-rotate-right'}
                                onClick={fetchAssignments}
                                variant={'outline-danger'}
                            >
                                <FormattedMessage defaultMessage="Retry" id="retry.225ca180" />
                            </GMetrixButton>
                        </div>
                    )
                    :
                    (
                        assignedAccessCodes.length === 0 && assignedClassrooms.length === 0 ?
                            (
                                <div className="d-flex flex-column align-items-center text-center py-5">
                                    <i className="fa-regular fa-circle-exclamation fs-1 text-muted mb-3" />
                                    <h5 className="fw-bold mb-1">
                                        <FormattedMessage defaultMessage="Custom Test Not Assigned" id="custom_test_not_assigned.fb90ff77" />
                                    </h5>
                                    {/* The Get endpoint counts assignments across every partner; the lists above are scoped to the currently managed partner. */}
                                    <p className="text-muted mb-3">
                                        {totalAssignmentCount > 0
                                            ? (formatMessage({ id: 'this_custom_test_is_only_assigned_under.f3289153', defaultMessage: 'This Custom Test is only assigned under other partner accounts. Assignments made while managing this partner will appear here.' }))
                                            : (formatMessage({ id: 'this_custom_test_has_not_been_assigned_t.feff03f1', defaultMessage: 'This Custom Test has not been assigned to any Classrooms or Access Codes' }))}
                                    </p>
                                    {isActive && isOwner && permissions.CanAssign && (
                                        <GMetrixButton
                                            iconClass="fa-solid fa-user-plus"
                                            onClick={launchAssignmentWizard}
                                            variant="primary"
                                        >
                                            <FormattedMessage defaultMessage="Assign" id="assign.0dabb6f2" />
                                        </GMetrixButton>
                                    )}
                                </div>
                            )
                            :
                            (
                                <div className={'d-flex flex-column gap-3'}>

                                    {assignedClassrooms.map(classroom => {

                                        const detail = detailFor('group', classroom.Id);

                                        return renderAssignmentCard(
                                            `classroom-${classroom.Id}`,
                                            [
                                                {
                                                    label: formatMessage({ id: 'assigned_to.b569d5f8', defaultMessage: 'Assigned to' }),
                                                    value: (
                                                        <Link className={'text-decoration-underline'} to={`/classrooms/${classroom.Id}`}>
                                                            {classroom.Name}
                                                        </Link>
                                                    )
                                                },
                                                { label: formatMessage({ id: 'dates.f6f73a3f', defaultMessage: 'Dates' }), value: renderDates(detail) },
                                                { label: formatMessage({ id: 'max_attempts.3822990c', defaultMessage: 'Max Attempts' }), value: renderMaxAttempts(detail) }
                                            ],
                                            [
                                                { label: formatMessage({ id: 'type.a129b9e6', defaultMessage: 'Type' }), value: formatMessage({ id: 'classroom.127e40cb', defaultMessage: 'Classroom' }) },
                                                { label: formatMessage({ id: 'mode.f65b1f2b', defaultMessage: 'Mode' }), value: getModeValue(modeId) },
                                                { label: formatMessage({ id: 'features.a1804c57', defaultMessage: 'Features' }), value: renderFeatures(detail) }
                                            ]
                                        );

                                    })}

                                    {assignedAccessCodes.map(accessCode => {

                                        const detail = detailFor('transaction', accessCode.Id);

                                        return renderAssignmentCard(
                                            `access-code-${accessCode.Id}`,
                                            [
                                                {
                                                    label: formatMessage({ id: 'assigned_to.b569d5f8', defaultMessage: 'Assigned to' }),
                                                    value: (
                                                        <Link className={'text-decoration-underline'} to={`/access-codes/${accessCode.Id}`}>
                                                            {accessCode.Identifier}
                                                        </Link>
                                                    )
                                                },

                                                // Access-code assignments carry no date restrictions, so the Dates row is omitted (mock).
                                                { label: formatMessage({ id: 'max_attempts.3822990c', defaultMessage: 'Max Attempts' }), value: renderMaxAttempts(detail) },
                                                { label: formatMessage({ id: 'features.a1804c57', defaultMessage: 'Features' }), value: renderFeatures(detail) }
                                            ],
                                            [
                                                { label: formatMessage({ id: 'type.a129b9e6', defaultMessage: 'Type' }), value: formatMessage({ id: 'access_code.b5fc1b57', defaultMessage: 'Access Code' }) },
                                                { label: formatMessage({ id: 'mode.f65b1f2b', defaultMessage: 'Mode' }), value: getModeValue(modeId) }
                                            ]
                                        );

                                    })}

                                </div>
                            )
                    )
            }

            <AssignWizard ref={assignWizardRef} />

        </div>
    );

};
